import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import brandConfig from 'src/config/brandConfig';
import ConfirmDialog from '../../components/confirm-dialog';
import { StyledCardContent } from '../../components/containers/StyledCardContent';
import EmptyContent from '../../components/empty-content/EmptyContent';
import Iconify from '../../components/iconify';
import { GuestGeneral } from '../../models/GuestGeneral';
import { ReservationGeneral } from '../../models/ReservationGeneral';
import { useDeleteReservationGuestMutation } from '../../redux/rtkQuery/apiSlice';
import { checkAgeValid } from '../../utils/mrr/ageUtils';
import { cloud_getReservationGuests } from '../../utils/mrr/cloudFunctions';
import { StandardRTKQError } from '../error/StandardRTKQError';
import HelpLink from './HelpLinks';
import { RegistrationFooter } from './RegistrationFooter';

const rowBorderSx = {
  borderBottom: '1px solid',
  borderColor: 'lightgray',
};

interface RegistrationGuestsSectionProps {
  mainGuest: GuestGeneral;
  reservation: ReservationGeneral;
  ageRequired: boolean;
  guestModels: GuestGeneral[] | null;
  parentLoading: boolean;
  callbackCancel: VoidFunction;
  callbackAddGuest: VoidFunction;
  callbackEditGuest: (guest: GuestGeneral) => void;
  callbackGuestDeleted: (guest: GuestGeneral) => void;
  callbackContinue: VoidFunction;
}
export function RegistrationGuestsSection({
  mainGuest,
  reservation,
  ageRequired,
  guestModels = [],
  parentLoading,
  callbackCancel,
  callbackAddGuest,
  callbackEditGuest,
  callbackGuestDeleted,
  callbackContinue,
}: RegistrationGuestsSectionProps) {
  const isFetchingGuest = false;
  const isSuccessGuest = true;
  const dataGuest = Array.isArray(guestModels) ? guestModels : new Array<GuestGeneral>();
  const isErrorGuest = false;
  const errorGuest = null;

  const [
    sendDeleteReservationGuestMutation,
    { isLoading: isLoadingDelete, isError: isErrorDelete, error: errorDelete },
  ] = useDeleteReservationGuestMutation();

  const handleDeleteGuest = useCallback(
    async (guest: GuestGeneral) => {
      await sendDeleteReservationGuestMutation({
        reservationName: reservation.name,
        guestId: guest.id,
      })
        .then((response: any) => {
          if (response.error) {
            // eslint-disable-next-line no-useless-return
            return;
          }
          callbackGuestDeleted(guest);
        })
        .catch((deleteError: any) => {
          console.error('error on delete guest', deleteError);
        });
    },
    [callbackGuestDeleted, reservation.name, sendDeleteReservationGuestMutation]
  );

  const noGuests = isSuccessGuest && dataGuest.length === 0;
  let sortedGuestData: GuestGeneral[] = [];
  let maxGuests = 0;
  let totalGuests = 0;
  let allAgesSet = true;
  if (isSuccessGuest) {
    const cloned: GuestGeneral[] = [...dataGuest];
    sortedGuestData = cloned.sort((a, b) => {
      if (mainGuest) {
        if (mainGuest.id === a.id) {
          return -1;
        }
        if (mainGuest.id === b.id) {
          return 1;
        }
      }
      return a.guest_name.localeCompare(b.guest_name);
    });
    maxGuests = reservation.max_guests;
    totalGuests = sortedGuestData.length;
    sortedGuestData.forEach((guest: GuestGeneral) => {
      if (typeof guest.age !== 'number') {
        allAgesSet = false;
      }
    });
  }
  const allowAddGuest = totalGuests < maxGuests;
  const guestCountExceeded = totalGuests > maxGuests;
  const allowContinue = !guestCountExceeded && allAgesSet;

  if (isErrorGuest) {
    return (
      <Card raised>
        <StyledCardContent>
          <Stack spacing={2}>
            <Typography variant="h4">Guests</Typography>
            <StandardRTKQError
              mutationCall={false}
              error={errorGuest}
              endpoint={cloud_getReservationGuests}
            />
          </Stack>
        </StyledCardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Table size="small" aria-label="Guest list">
        <TableHead>
          <TableRow>
            <TableCell component="th" scope="col" sx={{ ...rowBorderSx, backgroundColor: 'transparent', color: 'text.secondary', typography: 'subtitle1', fontWeight: 600, px: 0 }}>
              Guest Name
            </TableCell>
            <TableCell component="th" scope="col" sx={{ ...rowBorderSx, backgroundColor: 'transparent', color: 'text.secondary', typography: 'subtitle1', fontWeight: 600, px: 0 }}>
              Age
            </TableCell>
            <TableCell
              component="th"
              scope="col"
              align="right"
              sx={{
                ...rowBorderSx,
                backgroundColor: 'transparent',
                px: 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                Actions
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {noGuests && (
            <TableRow>
              <TableCell colSpan={3} sx={{ borderBottom: 'none', px: 0 }}>
                <EmptyContent hideImg title="No guests." description="You may add guests below." />
              </TableCell>
            </TableRow>
          )}
          {!noGuests &&
            !isFetchingGuest &&
            sortedGuestData.map((guest, i) => {
              const { guest_id, id } = guest;
              if (mainGuest) {
                const isMainHost = mainGuest.id === id;
                if (isMainHost) {
                  return <MainHostEntry key={guest_id + '-' + i} guest={guest} />;
                }
              }
              return (
                <NonMainEntry
                  key={guest_id + '-' + i}
                  guest={guest}
                  handleDelete={() => {
                    handleDeleteGuest(guest);
                  }}
                  handleEdit={(guestSelection: GuestGeneral) => {
                    callbackEditGuest(guestSelection);
                  }}
                />
              );
            })}
        </TableBody>
      </Table>
      <Typography
        align="right"
        variant="subtitle1"
        component="p"
        color={guestCountExceeded ? '#d22' : undefined}
        role="status"
      >
        {'Guest Count: ' + totalGuests}{' '}
        <Typography color="text.secondary" variant="caption" component="span">
          {' '}
          (Max of {maxGuests})
        </Typography>
      </Typography>
      {!allAgesSet && (
        <Typography align="center" variant="subtitle1" component="p" color="#d22" role="status">
          Please enter an age for each guest.
        </Typography>
      )}
      {brandConfig.helpLinks.guest_registration && (
        <HelpLink label="guest_registration" value={brandConfig.helpLinks.guest_registration} />
      )}
      <RegistrationFooter
        backButton={
          <Button
            disabled={isLoadingDelete || parentLoading}
            onClick={() => {
              callbackCancel();
            }}
          >
            Back
          </Button>
        }
      >
        <Stack direction="row" spacing={2}>
          <Button
            color="secondary"
            disabled={!allowAddGuest || isLoadingDelete || parentLoading}
            variant="contained"
            onClick={callbackAddGuest}
          >
            Add Guest
          </Button>
          <LoadingButton
            loading={isLoadingDelete || parentLoading}
            disabled={!allowContinue || isLoadingDelete || parentLoading}
            variant="contained"
            onClick={callbackContinue}
          >
            Continue
          </LoadingButton>
        </Stack>
      </RegistrationFooter>
    </Stack>
  );
}

// local sub-components

function NonMainEntry({
  guest,
  handleDelete,
  handleEdit,
}: {
  guest: GuestGeneral;
  handleDelete: (guestSelection: GuestGeneral) => void;
  handleEdit: (guestSelection: GuestGeneral) => void;
}) {
  const { guest_name, age } = guest;
  const ageValid = checkAgeValid(age);
  const [openConfirm, setOpenConfirm] = useState(false);
  return (
    <>
      <TableRow>
        <TableCell sx={{ ...rowBorderSx, py: 1, px: 0 }}>
          <Typography variant="body1">{guest_name}</Typography>
        </TableCell>
        <TableCell sx={{ ...rowBorderSx, py: 1, px: 0 }}>
          {ageValid ? (
            <Typography variant="body1">{age}</Typography>
          ) : (
            <Typography variant="body1" color="#d20">
              Required
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ ...rowBorderSx, py: 1, px: 0 }} align="right">
          <Stack direction="row" justifyContent="flex-end" gap={2} alignItems="center">
            <IconButton
              color="primary"
              aria-label={`Edit ${guest_name}`}
              onClick={() => {
                handleEdit(guest);
              }}
            >
              <Iconify width={24} icon="mdi:edit-box" />
            </IconButton>
            <IconButton
              color="error"
              aria-label={`Remove ${guest_name}`}
              onClick={() => {
                setOpenConfirm(true);
              }}
            >
              <Iconify width={24} icon="mdi:delete-circle-outline" />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <ConfirmDialog
        open={openConfirm}
        action={
          <Button
            color="error"
            onClick={() => {
              handleDelete(guest);
              setOpenConfirm(false);
            }}
          >
            Remove
          </Button>
        }
        onClose={() => {
          setOpenConfirm(false);
        }}
        title="Remove Guest"
        content={`Are you sure you want to remove ${guest.guest_name}?`}
      />
    </>
  );
}

function MainHostEntry({ guest, userIsGuest }: { guest: GuestGeneral; userIsGuest?: boolean }) {
  const { guest_name, age } = guest;
  const textColor = ''; // '#888' - appear disabled
  return (
    <TableRow>
      <TableCell sx={{ ...rowBorderSx, borderTop: '1px solid', borderTopColor: 'lightgray', py: 2, px: 0 }}>
        <Typography variant="body1">{guest_name + ' (Host)'}</Typography>
      </TableCell>
      <TableCell sx={{ ...rowBorderSx, borderTop: '1px solid', borderTopColor: 'lightgray', py: 2, px: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {userIsGuest && <Iconify icon="mdi:user-star-outline" />}
          <Typography variant="body1" color={textColor}>
            {age}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ ...rowBorderSx, borderTop: '1px solid', borderTopColor: 'lightgray', py: 2, px: 0 }} />
    </TableRow>
  );
}
